import { Flex, Portal, Tag, TagLeftIcon } from '@chakra-ui/react';
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { HiBolt, HiBoltSlash } from 'react-icons/hi2';

type Context = {
  isConnected: boolean;
  onConnected: () => void;
  onDisconnected: () => void;
};

export const ConnectionContext = createContext({} as Context);

export const useConnection = () => useContext(ConnectionContext);

type ConnectionProviderProps = {
  children: ReactNode;
};

export const ConnectionProvider = ({ children }: ConnectionProviderProps) => {
  const [isConnected, setIsConnected] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  const showTag = useCallback(({ shouldPersist }) => {
    setIsVisible(true);
    setTimeout(() => {
      if (!shouldPersist) setIsVisible(false);
    }, 5000); // Show for 5 seconds
  }, []);

  const onConnected = useCallback(() => {
    console.log('Connected');
    setIsConnected(true);
    showTag({ shouldPersist: false });
  }, [showTag]);

  const onDisconnected = useCallback(() => {
    setIsConnected(false);
    console.log('Disconnected');
    showTag({ shouldPersist: true });
  }, [showTag]);

  return (
    <ConnectionContext.Provider value={{ isConnected, onConnected, onDisconnected }}>
      {children}
      <Portal>
        <ConnectionStatus isConnected={isConnected} isVisible={isVisible} />
      </Portal>
    </ConnectionContext.Provider>
  );
};

type ConnectionStatusProps = {
  isConnected: boolean;
  isVisible: boolean;
};

const ConnectionStatus = ({ isConnected, isVisible }: ConnectionStatusProps) => {
  const [hasMouseOver, setHasMouseOver] = useState(false);

  return (
    <Flex justifyContent="center" alignItems="center" position="fixed" width="full" bottom={4} pointerEvents="none">
      <Tag
        onMouseOver={() => setHasMouseOver(true)}
        onMouseOut={() => setHasMouseOver(false)}
        className={`animate__animated ${isVisible ? 'animate__bounceInUp' : hasMouseOver ? '' : 'animate__bounceOutDown'}`}
        zIndex="toast"
        colorScheme={isConnected ? 'green' : 'red'}
      >
        <TagLeftIcon as={isConnected ? HiBolt : HiBoltSlash} />
        {isConnected ? 'Connected' : 'Reconnecting...'}
      </Tag>
    </Flex>
  );
};
